import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { FiEdit, FiTrash2, FiMapPin, FiDownload, FiPrinter } from 'react-icons/fi';
import { Modal, Button, Spin, Empty, Input, Select, Space, Typography } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';

// 도로명주소 검색 컴포넌트
const AddressSearch = ({ onAddressSelect }) => {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setErrorMessage('검색어를 입력해주세요');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      
      // 개발/테스트 환경에서는 더미 데이터 사용
      // 실제 환경에서는 API 호출 사용
      // if (process.env.NODE_ENV === 'development') {
      //   // 개발 환경에서는 임시 데이터 제공
      //   setTimeout(() => {
      //     const dummyResults = [
      //       {
      //         roadAddr: '서울특별시 중구 세종대로 110',
      //         jibunAddr: '서울특별시 중구 태평로1가 31',
      //         zipNo: '04524'
      //       },
      //       {
      //         roadAddr: '서울특별시 종로구 세종대로 172',
      //         jibunAddr: '서울특별시 종로구 세종로 1-58',
      //         zipNo: '03172'
      //       },
      //       {
      //         roadAddr: '경기도 성남시 분당구 대왕판교로 645번길 12',
      //         jibunAddr: '경기도 성남시 분당구 삼평동 625',
      //         zipNo: '13493'
      //       }
      //     ];
      //     setResults(dummyResults);
      //     setIsLoading(false);
      //   }, 500);
      // } else {
        // 실제 환경에서는 도로명주소 API 호출
        // 참고: API 키는 환경변수로 관리하는 것이 좋습니다
        try {
          // 백엔드를 통한 주소 검색 API 호출
          const response = await axios.get('api/address/search', {
            params: { keyword }
          });
          
          if (response.data && response.data.results) {
            setResults(response.data.results);
          } else {
            setResults([]);
            setErrorMessage('검색 결과가 없습니다');
          }
        } catch (error) {
          console.error('주소 검색 API 오류:', error);
          setErrorMessage('주소 검색 서비스 이용 중 오류가 발생했습니다');
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      // }
    } catch (error) {
      console.error('주소 검색 오류:', error);
      setIsLoading(false);
      setErrorMessage('주소 검색 중 오류가 발생했습니다');
    }
  };

  // 주소 선택 시 위도/경도 정보 추가 (임의로 설정)
  const handleAddressSelect = (address) => {
    // 주소 선택 시 좌표 정보 추가 (위치 정보는 임의로 생성)
    const latitude = 37.5 + (parseFloat(address.admCd || '0') % 10) * 0.01;
    const longitude = 127.0 + (parseFloat(address.rnMgtSn || '0') % 10) * 0.01;
    
    const locationInfo = {
      address: address.roadAddr,
      jibunAddress: address.jibunAddr,
      zipcode: address.zipNo,
      latitude: latitude,
      longitude: longitude,
      // 추가 정보 저장
      admCd: address.admCd,
      rnMgtSn: address.rnMgtSn,
      siNm: address.siNm,
      sggNm: address.sggNm,
      emdNm: address.emdNm,
      bdNm: address.bdNm
    };
    
    onAddressSelect(locationInfo);
  };

  return (
    <div className="mb-4">
      <div className="flex gap-2 mb-2">
        <Input 
          value={keyword} 
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="주소 검색" 
          onPressEnter={handleSearch}
        />
        <Button onClick={handleSearch} loading={isLoading}>검색</Button>
      </div>
      
      {errorMessage && <p className="text-red-500 text-sm">{errorMessage}</p>}
      
      <div className="max-h-40 overflow-y-auto border rounded-md">
        {results.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {results.map((item, index) => (
              <li 
                key={index} 
                className="p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleAddressSelect(item)}
              >
                <div className="font-medium">{item.roadAddr}</div>
                <div className="text-xs text-gray-500">{item.jibunAddr} ({item.zipNo})</div>
              </li>
            ))}
          </ul>
        ) : (
          !isLoading && !errorMessage && <p className="p-2 text-gray-500 text-center">검색 결과가 여기에 표시됩니다</p>
        )}
      </div>
    </div>
  );
};

// QR 코드 렌더링 컴포넌트
const QRCodeRenderer = ({ value, title, url }) => {
  if (!value) {
    return (
      <div className="w-[250px] h-[250px] flex items-center justify-center bg-gray-100">
        <p className="text-gray-500">QR 코드 데이터가 없습니다.</p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center">
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <QRCodeSVG 
        value={url || value}
        size={250}
        level="H"
      />
      {url && (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="mt-3 text-blue-500 hover:text-blue-700 text-sm break-all text-center"
        >
          {url}
        </a>
      )}
    </div>
  );
};

const ClassroomManagement = () => {
  const { authAxios, user } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingClassroom, setIsAddingClassroom] = useState(false);
  const [isEditingClassroom, setIsEditingClassroom] = useState(false);
  const [currentClassroom, setCurrentClassroom] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [qrCodes, setQrCodes] = useState({ checkinQR: null, checkoutQR: null });
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  
  const printRef = useRef();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  // 강의실 목록 가져오기
  const fetchClassrooms = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // API 경로 수정
      const response = await authAxios.get('classrooms');
      
      const mappedClassrooms = response.data.map(classroom => ({
        ...classroom,
        id: classroom._id || classroom.id,
      }));
      
      setClassrooms(mappedClassrooms);
      setIsLoading(false);
    } catch (err) {
      console.error('강의실 목록 조회 오류:', err);
      setError('강의실 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      
      // 개발용 임시 데이터
      setClassrooms([
        { 
          id: '1', 
          name: '301호', 
          location: { 
            latitude: 37.5665, 
            longitude: 126.9780, 
            address: '서울특별시 중구 세종대로 110' 
          },
          description: '컴퓨터실' 
        },
        { 
          id: '2', 
          name: '302호', 
          location: { 
            latitude: 37.5665, 
            longitude: 126.9780, 
            address: '서울특별시 중구 세종대로 110' 
          },
          description: '세미나실' 
        },
      ]);
    }
  }, [authAxios]);

  // 초기 데이터 로딩
  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  // 강의실 추가 모달 열기
  const openAddClassroomModal = () => {
    setIsAddingClassroom(true);
    setSelectedAddress(null);
    reset();
  };

  // 강의실 정보 편집 시작
  const startEditClassroom = (classroom) => {
    setCurrentClassroom(classroom);
    setIsEditingClassroom(true);
    setSelectedAddress(classroom.location);
    
    setValue('name', classroom.name);
    setValue('category', classroom.category || '');
    setValue('description', classroom.description || '');
  };

  // 강의실 추가 제출 처리
  const onAddClassroom = async (data) => {
    if (!selectedAddress) {
      toast.error('강의실 위치를 선택해주세요.');
      return;
    }
    
    try {
      const newClassroom = {
        name: data.name,
        category: data.category,
        description: data.description,
        location: selectedAddress
      };
      
      // API 경로 수정
      const response = await authAxios.post('/classrooms', newClassroom);
      
      toast.success('강의실이 추가되었습니다.');
      setIsAddingClassroom(false);
      
      // 강의실 목록 새로고침
      fetchClassrooms();
    } catch (err) {
      console.error('강의실 추가 오류:', err);
      toast.error('강의실 추가 중 오류가 발생했습니다.');
    }
  };

  // 강의실 편집 제출 처리
  const onEditClassroom = async (data) => {
    if (!selectedAddress) {
      toast.error('강의실 위치를 선택해주세요.');
      return;
    }
    
    try {
      const updatedClassroom = {
        name: data.name,
        category: data.category,
        description: data.description,
        location: selectedAddress
      };
      
      // API 경로 수정
      await authAxios.put(`/classrooms/${currentClassroom.id}`, updatedClassroom);
      
      toast.success('강의실 정보가 수정되었습니다.');
      setIsEditingClassroom(false);
      
      // 강의실 목록 새로고침
      fetchClassrooms();
    } catch (err) {
      console.error('강의실 수정 오류:', err);
      toast.error('강의실 수정 중 오류가 발생했습니다.');
    }
  };

  // 강의실 삭제 처리
  const deleteClassroom = async (id) => {
    if (window.confirm('정말로 이 강의실을 삭제하시겠습니까?')) {
      try {
        // API 경로 수정
        await authAxios.delete(`/classrooms/${id}`);
        
        toast.success('강의실이 삭제되었습니다.');
        
        // 강의실 목록 새로고침
        fetchClassrooms();
      } catch (err) {
        console.error('강의실 삭제 오류:', err);
        toast.error('강의실 삭제 중 오류가 발생했습니다.');
      }
    }
  };

  // QR 코드 생성 처리
  const generateQRCodes = async (classroom) => {
    try {
      setQrLoading(true);
      setCurrentClassroom(classroom);
      
      try {
        // 먼저 기존 QR 코드가 있는지 확인
        const getResponse = await authAxios.get(`/classrooms/${classroom.id}/qr`);
        
        // 기존 QR 코드가 있으면 바로 표시
        setQrCodes({
          checkinQR: getResponse.data.checkinQR,
          checkoutQR: getResponse.data.checkoutQR,
          checkinUrl: getResponse.data.checkinUrl,
          checkoutUrl: getResponse.data.checkoutUrl,
          expiresAt: getResponse.data.expiresAt
        });
        
        setShowQRModal(true);
        setQrLoading(false);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // QR 코드가 없는 경우 새로 생성
          try {
            // API 경로 수정
            const response = await authAxios.post(`/classrooms/${classroom.id}/generate-qr`, {
              instructorId: user?._id || user?.id
            });
            
            setQrCodes({
              checkinQR: response.data.checkinQR,
              checkoutQR: response.data.checkoutQR,
              checkinUrl: response.data.checkinUrl,
              checkoutUrl: response.data.checkoutUrl,
              expiresAt: response.data.expiresAt
            });
            
            setShowQRModal(true);
            setQrLoading(false);
          } catch (genError) {
            console.error('QR 코드 생성 오류:', genError);
            toast.error('QR 코드 생성 중 오류가 발생했습니다.');
            setQrLoading(false);
          }
        } else {
          console.error('QR 코드 조회 오류:', error);
          toast.error('QR 코드 조회 중 오류가 발생했습니다.');
          setQrLoading(false);
        }
      }
    } catch (err) {
      console.error('QR 코드 조회/생성 오류:', err);
      toast.error('QR 코드 조회/생성 중 오류가 발생했습니다.');
      setQrLoading(false);
    }
  };

  // QR 코드 다운로드
  const downloadQRCode = (type) => {
    const canvas = document.querySelector(`#${type}-qr-code canvas`);
    if (!canvas) return;
    
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `${currentClassroom.name}_${type === 'checkin' ? '입실' : '퇴실'}_QR.png`;
    link.href = url;
    link.click();
  };

  // QR 코드 인쇄
  const printQRCode = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const originalContents = document.body.innerHTML;
    const printContents = printContent.innerHTML;
    
    document.body.innerHTML = `
      <div style="text-align: center; padding: 20px;">
        <h1 style="margin-bottom: 20px;">${currentClassroom.name} 강의실 QR 코드</h1>
        ${printContents}
      </div>
    `;
    
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">강의실 관리</h1>
        <Button 
          type="primary" 
          onClick={openAddClassroomModal}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          강의실 추가
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin size="large" />
        </div>
      ) : classrooms.length === 0 ? (
        <Empty description="등록된 강의실이 없습니다." />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  구분
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  강의실명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  위치
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  설명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {classrooms.map((classroom) => (
                <tr key={classroom.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{classroom.category || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{classroom.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{classroom.location?.address}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{classroom.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => generateQRCodes(classroom)}
                        className="text-blue-600 hover:text-blue-900"
                        disabled={qrLoading}
                      >
                        QR 조회
                      </button>
                      <button
                        onClick={() => startEditClassroom(classroom)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => deleteClassroom(classroom.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 강의실 추가 모달 */}
      <Modal
        title="강의실 추가"
        open={isAddingClassroom}
        onCancel={() => setIsAddingClassroom(false)}
        footer={null}
      >
        <form onSubmit={handleSubmit(onAddClassroom)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              구분
            </label>
            <input
              {...register('category')}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="예: 서울캠퍼스, 부산캠퍼스 등"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              강의실명
            </label>
            <input
              {...register('name', { required: '강의실명은 필수입니다.' })}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              강의실 위치
            </label>
            <AddressSearch onAddressSelect={setSelectedAddress} />
            {selectedAddress && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm">
                  <strong>주소:</strong> {selectedAddress.address}
                </p>
                <p className="text-sm">
                  <strong>좌표:</strong> {selectedAddress.latitude}, {selectedAddress.longitude}
                </p>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명 (선택사항)
            </label>
            <textarea
              {...register('description')}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button onClick={() => setIsAddingClassroom(false)}>
              취소
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              추가
            </Button>
          </div>
        </form>
      </Modal>

      {/* 강의실 편집 모달 */}
      <Modal
        title="강의실 정보 수정"
        open={isEditingClassroom}
        onCancel={() => setIsEditingClassroom(false)}
        footer={null}
      >
        <form onSubmit={handleSubmit(onEditClassroom)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              구분
            </label>
            <input
              {...register('category')}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="예: 서울캠퍼스, 부산캠퍼스 등"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              강의실명
            </label>
            <input
              {...register('name', { required: '강의실명은 필수입니다.' })}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              강의실 위치
            </label>
            <AddressSearch onAddressSelect={setSelectedAddress} />
            {selectedAddress && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm">
                  <strong>주소:</strong> {selectedAddress.address}
                </p>
                <p className="text-sm">
                  <strong>좌표:</strong> {selectedAddress.latitude}, {selectedAddress.longitude}
                </p>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명 (선택사항)
            </label>
            <textarea
              {...register('description')}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button onClick={() => setIsEditingClassroom(false)}>
              취소
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              저장
            </Button>
          </div>
        </form>
      </Modal>

      {/* QR 코드 모달 */}
      <Modal
        title={`${currentClassroom?.name || '강의실'} QR 코드`}
        open={showQRModal}
        onCancel={() => setShowQRModal(false)}
        width={700}
        footer={(
          <div className="flex justify-between">
            <Space>
              <Button icon={<DownloadOutlined />} onClick={() => downloadQRCode('checkin')}>
                입실 QR 저장
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => downloadQRCode('checkout')}>
                퇴실 QR 저장
              </Button>
            </Space>
            <Button icon={<PrinterOutlined />} onClick={printQRCode}>
              QR 코드 인쇄
            </Button>
          </div>
        )}
      >
        {/* <div className="mb-2 text-center">
          <Typography.Text type="secondary">
            유효기간: {qrCodes.expiresAt ? new Date(qrCodes.expiresAt).toLocaleString() : '정보 없음'}
          </Typography.Text>
        </div> */}
        <div ref={printRef} className="flex justify-around">
          <div id="checkin-qr-code" className="flex flex-col items-center">
            <QRCodeRenderer 
              value={qrCodes.checkinQR} 
              url={qrCodes.checkinUrl}
              title="입실 QR 코드" 
            />
            <p className="mt-2 text-center text-gray-500">
              {currentClassroom?.location?.address || '주소 정보 없음'}
            </p>
          </div>
          
          <div id="checkout-qr-code" className="flex flex-col items-center">
            <QRCodeRenderer 
              value={qrCodes.checkoutQR}
              url={qrCodes.checkoutUrl}
              title="퇴실 QR 코드" 
            />
            <p className="mt-2 text-center text-gray-500">
              {currentClassroom?.location?.address || '주소 정보 없음'}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClassroomManagement; 